import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { captureSignupSource } from "./lib/signupSource";
const CACHE_RESET_PARAM = "cache-reset";
const PREVIEW_REFRESH_PARAM = "preview-refresh";
const PREVIEW_VERSION_ENDPOINT = "/__preview_version";
const PREVIEW_VERSION_STORAGE_KEY = "listbright-preview-version";
const PREVIEW_RESUME_REFRESH_MS = 30_000;

function isPreviewLikeHostname(hostname: string) {
  return (
    hostname.includes("id-preview--") ||
    hostname.includes("lovableproject.com") ||
    hostname.includes("localhost") ||
    hostname.includes("127.0.0.1") ||
    hostname.includes("webcontainer")
  );
}

function cleanupQueryParams(...paramNames: string[]) {
  const url = new URL(window.location.href);
  let changed = false;

  for (const paramName of paramNames) {
    if (url.searchParams.has(paramName)) {
      url.searchParams.delete(paramName);
      changed = true;
    }
  }

  if (!changed) return;

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(null, "", nextUrl);
}

function reloadPreview() {
  const url = new URL(window.location.href);
  url.searchParams.delete(CACHE_RESET_PARAM);
  url.searchParams.delete(PREVIEW_REFRESH_PARAM);

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (nextUrl !== currentUrl) {
    window.location.replace(nextUrl);
    return;
  }

  window.location.reload();
}

async function fetchPreviewVersion() {
  try {
    const response = await fetch(
      `${PREVIEW_VERSION_ENDPOINT}?t=${Date.now()}`,
      {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-store",
        },
      },
    );

    if (!response.ok) return null;

    const version = (await response.text()).trim();
    return version || null;
  } catch (error) {
    console.warn("[ListBright] Preview version check failed", error);
    return null;
  }
}

async function syncPreviewVersion() {
  if (!isPreviewLikeHostname(window.location.hostname)) return false;

  const latestVersion = await fetchPreviewVersion();
  if (!latestVersion) return false;

  const previousVersion = window.sessionStorage.getItem(
    PREVIEW_VERSION_STORAGE_KEY,
  );

  window.sessionStorage.setItem(PREVIEW_VERSION_STORAGE_KEY, latestVersion);

  if (previousVersion && previousVersion !== latestVersion) {
    reloadPreview();
    return true;
  }

  return false;
}

async function installPreviewFreshnessGuard() {
  if (!isPreviewLikeHostname(window.location.hostname)) return false;

  if (await syncPreviewVersion()) return true;

  const navigationEntry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (navigationEntry?.type === "back_forward") {
    if (await syncPreviewVersion()) return true;
  }

  window.addEventListener("pageshow", () => {
    void syncPreviewVersion();
  });

  window.addEventListener("focus", () => {
    void syncPreviewVersion();
  });

  let hiddenAt = 0;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }

    if (!hiddenAt) return;

    const hiddenDuration = Date.now() - hiddenAt;
    hiddenAt = 0;

    if (hiddenDuration >= PREVIEW_RESUME_REFRESH_MS) {
      void syncPreviewVersion();
    }
  });

  return false;
}

async function clearLegacyCaches() {
  let cleared = false;

  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
      if (registrations.length > 0) cleared = true;
    } catch (e) {
      console.warn("[ListBright] SW unregister failed", e);
    }
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
      if (keys.length > 0) cleared = true;
    } catch (e) {
      console.warn("[ListBright] Cache clear failed", e);
    }
  }

  return cleared;
}

async function bootstrap() {
  if (await installPreviewFreshnessGuard()) return;

  const cleared = await clearLegacyCaches();

  if (cleared) {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CACHE_RESET_PARAM)) {
      url.searchParams.set(CACHE_RESET_PARAM, "1");
      window.location.replace(url.toString());
      return;
    }
  }

  cleanupQueryParams(CACHE_RESET_PARAM, PREVIEW_REFRESH_PARAM);

  // Capture signup source as early as possible (before any internal nav).
  captureSignupSource();

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error("[ListBright] Root element not found!");
    return;
  }

  const appTree = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // If react-snap prerendered HTML into #root at build time, hydrate it.
  // Otherwise mount from scratch (dev, SPA-only routes, or failed prerender).
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, appTree);
  } else {
    createRoot(rootElement).render(appTree);
  }
}

void bootstrap();
